import { Check, Loader2, MoreHorizontal, Plus, Gem } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import type { IntegrationTileProps } from '@/types/components/connectors/marketplace'
import { LibrarySourceMark } from './LibrarySourceMark'
import { integrationSummaryKey } from './librarySourceCatalog'

export function IntegrationTile({ connector, busy, requested, onDetails, onConnect, onRequest }: IntegrationTileProps) {
  const { t } = useTranslation('app')
  const premium = !connector.available && !connector.authenticated
  const action = connector.authenticated ? onDetails : connector.available ? onConnect : onRequest
  const actionLabel = connector.authenticated ? 'manageName' : connector.available ? 'connectName' : 'requestName'
  const Icon = busy ? Loader2 : connector.authenticated ? MoreHorizontal : requested ? Check : Plus
  return (
    <article
      className="group flex min-w-0 items-center gap-2 rounded-2xl px-3 py-4 transition-colors hover:bg-muted/60 focus-within:bg-muted/60"
      data-testid={`library-source-card-${connector.slug}`}
      data-state={connector.authenticated ? 'active' : 'inactive'}
    >
      <button type="button" onClick={onDetails} className="flex min-w-0 flex-1 items-center gap-4 rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={t('connectors.integrationDetails', { name: connector.name })} data-testid={`library-source-details-${connector.slug}`}>
        <LibrarySourceMark slug={connector.slug} logoUrl={connector.logoUrl} />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Text as="span" size="base" className="truncate font-normal">{connector.name}</Text>
            {premium && <span className="inline-flex shrink-0 items-center text-primary" title={t('connectors.premium')} data-testid={`integration-premium-${connector.slug}`}>
              <Gem className="size-4" role="img" aria-label={t('connectors.premium')} />
            </span>}
            {connector.authenticated && <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />{t('connectors.active')}
            </span>}
          </span>
          <Text as="span" size="sm" color="muted" className="line-clamp-2 leading-relaxed">{t(integrationSummaryKey(connector.slug), { name: connector.name })}</Text>
        </span>
      </button>
      <button type="button" onClick={action} disabled={busy || (premium && requested)}
        className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl text-foreground/70 outline-none transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-50', busy && 'cursor-wait')}
        aria-label={requested ? t('connectors.requestBadge') : t(`connectors.${actionLabel}`, { name: connector.name })}
        data-testid={connector.authenticated ? `library-source-manage-${connector.slug}` : connector.available ? `library-source-connect-${connector.slug}` : `library-source-request-${connector.slug}`}>
        <Icon className={cn('size-5', busy && 'animate-spin')} aria-hidden="true" />
      </button>
    </article>
  )
}
